import { Magic } from 'magic-sdk';
import { SolanaExtension } from '@magic-ext/solana';
import {
	PublicKey,
	Transaction,
	TransactionVersion,
	VersionedTransaction,
} from '@solana/web3.js';
import Env from 'src/environmentVariables/EnvironmentVariables';
import {
	BaseMessageSignerWalletAdapter,
	WalletName,
	WalletReadyState,
} from '@solana/wallet-adapter-base';
import {
	DRIFT_CUSTOM_WALLET_ICONS,
	DRIFT_CUSTOM_WALLET_OPTIONS,
} from 'src/constants/wallets';
import { InstanceWithExtensions, SDKBase } from '@magic-sdk/provider';

export interface MagicAuthLoginOptions {
	email: string;
}

class MagicAuthAdapter extends BaseMessageSignerWalletAdapter {
	signMessage(_message: Uint8Array): Promise<Uint8Array> {
		return this.magic.solana.signMessage(_message);
	}
	url = '';
	readyState: WalletReadyState = WalletReadyState.NotDetected;
	connecting = false;
	supportedTransactionVersions?: ReadonlySet<TransactionVersion>;

	public name =
		DRIFT_CUSTOM_WALLET_OPTIONS.MAGIC_AUTH_WALLET_ADAPTER_NAME as WalletName;
	public icon = DRIFT_CUSTOM_WALLET_ICONS.MAGIC_AUTH_WALLET_ADAPTER;

	// The type for magic is "InstanceWithExtensions" from magic-sdk
	// But they didn't export that type from the package :(
	private magic: InstanceWithExtensions<SDKBase, SolanaExtension[]>;

	public publicKey: PublicKey | null = null;
	public checkingLoginStatus: boolean | undefined;
	public isLoggedIn: boolean | undefined;
	public autoApprove = true;

	public constructor(rpcUrl: string) {
		super();

		this.magic = new Magic(Env.magicAuthApiKey, {
			extensions: [
				new SolanaExtension({
					rpcUrl,
				}),
			],
		});

		this.updateIsLoggedIn();
	}

	public async updateIsLoggedIn() {
		this.checkingLoginStatus = true;
		this.isLoggedIn = await this.magic.user.isLoggedIn();
		this.checkingLoginStatus = false;
	}

	public async signTransaction<T extends Transaction | VersionedTransaction>(
		tx: T
	): Promise<T> {
		const serializeConfig = {
			requireAllSignatures: false,
			verifySignatures: true,
		};

		// @ts-ignore
		const { signature: signatures } = await this.magic?.solana?.signTransaction(
			tx,
			serializeConfig
		);

		// The above signTransaction serializes the tx already, which we don't want to happen yet here, so just add the signatures it generated to the original tx and return that
		// Also, retryTxSender.ts in the sdk expects the tx to be modified in place when the wallet signs it instead of getting a new one returned, so this also avoids needing to update the sdk
		signatures?.forEach(
			(
				signature: Buffer | { signature: Buffer; publickey: PublicKey } // Really strange behaviour but sometimes (only on remote, not local, in my testing) the signature comes back with a different interface. Need to handle both cases
			) => {
				// @ts-expect-error
				if (signature?.signature) {
					// @ts-expect-error
					tx.addSignature(this.publicKey, signature.signature);
				} else {
					if (signature)
						tx.addSignature(
							this.publicKey as PublicKey,
							signature as Buffer & Uint8Array
						);
				}
			}
		);

		return tx;
	}

	public async signAllTransactions<
		T extends Transaction | VersionedTransaction,
	>(txes: T[]): Promise<T[]> {
		const signedTxes = await Promise.all(
			txes.map(async (tx) => await this.signTransaction(tx))
		);
		return signedTxes;
	}

	// Extra step required for this adapter:
	// If the user selected magic as the wallet, instead of just calling connect, you have to allow the user to either enter their email or click one of the social login buttons
	// After they do that, then call connect and pass in the results (e.g. their email address or social auth token)
	public connect = async (options?: MagicAuthLoginOptions): Promise<void> => {
		const emailToSend = options?.email?.replace(/\+[^@]*/, '');
		await this.magic.auth.loginWithEmailOTP({
			// showUI: false,
			email: emailToSend ?? '',
		});
		const metaData = await (this.magic.user as any)?.getInfo();

		if (!metaData?.publicAddress) {
			console.error(
				'MagicAuthAdapter.connect ~ metaData?.publicAddress is undefined'
			);
			return;
		}
		const newPubkey = new PublicKey(metaData?.publicAddress);
		this.publicKey = newPubkey;
		this.emit('connect', newPubkey);
	};

	public disconnect = async () => {
		await this.magic?.user?.logout();
		window.localStorage?.removeItem('preferredWallet');
		this.publicKey = null;
		this.isLoggedIn = false;
		this.emit('disconnect');
	};
}

const createMagicWalletAdapter = (rpcUrl: string) => {
	const adapter = new MagicAuthAdapter(rpcUrl);
	return adapter;
};

export default createMagicWalletAdapter;
